import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '@parrot/utils';
import styles from './Notice.module.css';

type Props = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  /** Leading icon. A `ReactNode` slot — `libs/ui` carries no icon dependency. */
  icon?: ReactNode;
  /** Bold lead-in above the message. */
  title?: ReactNode;
  children: ReactNode;
};

/**
 * An inline, persistent message about the thing it sits next to. For transient
 * notifications use a Toast.
 *
 * `danger` announces assertively (`role="alert"`), the rest politely
 * (`role="status"`) — an error surfacing after a failed submit has to reach screen
 * readers, and the reference had no role at all. Pass `role` to override.
 */
export const Notice = ({ tone = 'info', icon, title, children, className, ...divProps }: Props) => (
  <div
    role={tone === 'danger' ? 'alert' : 'status'}
    className={cx(styles.base, styles[tone], className)}
    {...divProps}
  >
    {icon !== undefined && (
      <span className={styles.icon} aria-hidden={'true'}>
        {icon}
      </span>
    )}
    <div className={styles.content}>
      {title !== undefined && <strong className={styles.title}>{title}</strong>}
      <div>{children}</div>
    </div>
  </div>
);
